import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output, TemplateRef } from '@angular/core';
import { Document } from '@common/entities';
import { Subject } from 'rxjs';

import { FileService } from '../shared/services/file/file.service';
import { DocumentDuplicateCheckService } from '../shared/services/document-upload/document-duplicate-check.service';

@Component({
  standalone: false,
  selector: 'common-document-list',
  templateUrl: './document-list.component.html',
  styleUrls: ['./document-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class DocumentListComponent implements OnDestroy {
  @Input() parentId: string;
  @Input() files: Document[];
  @Input() readOnlyList: string[] = [];
  @Input() categoryDisplay: string;
  @Input() tooltipMessage: string;
  @Input() tooltipPosition: string;
  @Input() ariaLabelForPreviewIcon: string;
  @Input() hideRemoveButton = false;
  @Input() showDateUploaded = false;
  @Input() errorMessageTemplates?: { [key: string]: TemplateRef<any> };
  @Input() validateDuplicate = false;
  @Output() removeFile = new EventEmitter<Document>();
  private destroy$ = new Subject<void>();
  existingDocErrorMessages: any[];

  constructor(
    private fileService: FileService,
    private documentDuplicateCheckService: DocumentDuplicateCheckService
  ) {}
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
  onRemoveFile(document: Document) {
    document.isDeleted = true;
    this.removeFile.emit(document);
  }

  /**
   * Opens a new window and displays a document using a blob stream.
   */
  onViewBlob(documentKey: string) {
    this.fileService.openBlobInNewWindow(documentKey, this.parentId);
  }

  isReadOnly(key: string) {
    return !!this.readOnlyList.find((a) => a === key);
  }

  getErrorTemplates(errors: Record<string, unknown> = {}): Array<TemplateRef<any>> {
    return Object.keys(errors)
      .map(key => this.errorMessageTemplates?.[key])
      .filter((t): t is TemplateRef<any> => !!t);
  }

  hasFileErrors(errors: Record<string, unknown> = {}, error: string) {
    if (this.errorMessageTemplates?.[error]) {
      return false;
    }
    return Object.keys(errors).indexOf(error) !== -1;
  }

  getFileDuplicateError(document) {
    if (this.validateDuplicate) {
      const existingDoc = this.documentDuplicateCheckService.findDuplicateDoc(document.hash);
      if (existingDoc) {
        if (existingDoc && existingDoc.pageName) {
          return `Document ${document.name} has already been uploaded in ${existingDoc.pageName}`;
        } else {
          return `Document ${document.name} has already been uploaded in this page`;
        }
      }
    }
  }
}
